import { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../services/api';
import { showError } from '../utils/toast';

const UserNotifications = ({ userEmail, isVisible, onClose, notifications = [] }) => {
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      const response = await userAPI.getNotifications(userEmail);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      showError('❌ Failed to refresh notifications');
      return [];
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const handleRefresh = async () => {
    const updatedNotifications = await fetchNotifications();
    if (updatedNotifications.length > 0) {
      // The parent component should update the notifications state
      // We'll show a success message to indicate the refresh was successful
      showError('✅ Notifications refreshed');
    }
  };

  // Handle mobile touch events
  const handleTouchStart = useCallback((e) => {
    const touchStartY = e.touches[0].clientY;
    const handleTouchMove = (e) => {
      const touchY = e.touches[0].clientY;
      const diff = touchStartY - touchY;
      // If swiping up more than 100px, close the modal
      if (diff > 100) {
        onClose();
        document.removeEventListener('touchmove', handleTouchMove);
      }
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => document.removeEventListener('touchmove', handleTouchMove);
  }, [onClose]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="modal-content bg-white rounded-t-2xl sm:rounded-2xl border border-yellow-200 w-full sm:w-auto sm:max-w-lg max-h-[80vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl transform transition-transform duration-300 ease-out"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        style={{
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        }}
      >
        <div className="sticky top-0 bg-white z-10 p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">🔔 Your Notifications</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl p-1 -mr-2"
            aria-label="Close notifications"
          >
            &times;
          </button>
        </div>

        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Your Notifications</h3>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="text-sm text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
            >
              {loading ? (
                <span className="inline-block h-4 w-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <span>↻</span>
              )}
              Refresh
            </button>
          </div>
          
          {loading && !notifications.length ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-yellow-400 border-t-transparent"></div>
              <p className="text-gray-600 mt-3 text-sm sm:text-base">Loading notifications...</p>
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                    <h4 className="font-semibold text-gray-800 text-sm sm:text-base break-words">
                      {notification.title}
                    </h4>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base whitespace-pre-line break-words">
                    {notification.message}
                  </p>
                  {notification.booking && (
                    <div className="mt-2 text-xs sm:text-sm text-gray-600">
                      Booking #{notification.booking}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-gray-600 text-base">No notifications yet</p>
              <p className="text-gray-500 text-sm mt-2">We'll let you know when something new arrives</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotifications;